import { AttachedFile, FileOwnerRef } from 'src/app/entities';

/**
 * @description Define las interfaces y tipos relacionados con la gestión de archivos multimedia en la aplicación, incluyendo la estructura de los archivos almacenados, las respuestas de las operaciones de carga y descarga, y la interfaz para adaptadores de almacenamiento de archivos.
 * - Esto proporciona una base sólida para implementar la funcionalidad de gestión de archivos multimedia en la aplicación, permitiendo una integración flexible con diferentes servicios de almacenamiento y facilitando la manipulación de archivos en diversos contextos dentro de la aplicación.
 * - La interfaz FileStorageAdapter, en particular, define un contrato para interactuar con servicios de almacenamiento de archivos, lo que permite una implementación modular y escalable de la funcionalidad de gestión de archivos multimedia.
 */
export interface StorageFileItemDto {
  Key?: string;
  id?: string;
  name?: string;
  [key: string]: unknown;
}

/**
 * @description Define la estructura de la respuesta al descargar un archivo en formato binario, que incluye los datos del archivo como un array de números.
 * - Esto permite manejar la descarga de archivos de manera eficiente, proporcionando una representación clara de los datos del archivo que se pueden procesar o guardar según sea necesario en la aplicación.
 */
export interface BinaryFileResponseDto {
  data: ArrayLike<number>;
}

/**
 * @description Define la estructura de la respuesta al cargar un archivo, que incluye información sobre el archivo cargado como su clave, ID y nombre, así como cualquier otra propiedad relevante.
 * - Esto permite manejar la respuesta de las operaciones de carga de archivos de manera consistente, proporcionando una representación clara de los resultados de la carga que se pueden utilizar para actualizar la interfaz de usuario o realizar otras acciones según sea necesario en la aplicación.
 * - La flexibilidad de incluir propiedades adicionales en la respuesta permite adaptarse a diferentes requisitos de almacenamiento y gestión de archivos, facilitando la integración con diversos servicios de almacenamiento y proporcionando una experiencia de usuario más rica al interactuar con archivos multimedia en la aplicación.
 * - Esta interfaz es fundamental para la implementación de la funcionalidad de carga de archivos, ya que proporciona una estructura clara para manejar los resultados de las operaciones de carga y permite una integración fluida con otros componentes de la aplicación que dependen de la gestión de archivos multimedia.
 */
export interface UploadFileResponseDto {
  key?: string;
  id?: string;
  name?: string;
  [key: string]: unknown;
}

/**
 * @description Define la estructura del contexto de carga de archivos, que incluye una referencia al propietario del archivo y el archivo adjunto que se va a cargar.
 * - Esto permite proporcionar toda la información necesaria para realizar una operación de carga de archivos, facilitando la construcción de la carga y asegurando que se asocie correctamente el archivo con su propietario en la aplicación.
 * - La inclusión de un contexto de carga estructurado mejora la claridad y la mantenibilidad del código relacionado con la gestión de archivos multimedia, permitiendo una implementación más robusta y escalable de la funcionalidad de carga de archivos en la aplicación.
 */
export interface UploadPayloadContext {
  owner: FileOwnerRef;
  file: AttachedFile;
}

/**
 * @description Define la interfaz para adaptadores de almacenamiento de archivos, que especifica los métodos necesarios para interactuar con servicios de almacenamiento de archivos, incluyendo la generación de URLs para listar, obtener, cargar y eliminar archivos, así como la construcción de la carga útil para las operaciones de carga y la resolución de claves, IDs y nombres de archivos remotos.
 * - Esta interfaz proporciona un contrato claro para implementar adaptadores de almacenamiento de archivos, lo que permite una integración flexible con diferentes servicios de almacenamiento y facilita la gestión de archivos multimedia en la aplicación.
 * - Al definir esta interfaz, se promueve una arquitectura modular y escalable para la gestión de archivos multimedia, permitiendo que diferentes implementaciones de almacenamiento de archivos se integren sin problemas con el resto de la aplicación, lo que mejora la mantenibilidad y la extensibilidad del código relacionado con la gestión de archivos multimedia.
 * - La implementación de esta interfaz es fundamental para la funcionalidad de gestión de archivos multimedia en la aplicación, ya que proporciona una estructura clara para interactuar con servicios de almacenamiento de archivos y manejar las operaciones relacionadas con los archivos de manera eficiente y consistente.
 */
export interface FileStorageAdapter {
  scope: FileOwnerRef['scope'];
  listFilesUrl: (owner: FileOwnerRef) => string;
  getFileUrl: (
    owner: FileOwnerRef,
    remoteFile: StorageFileItemDto
  ) => string;
  uploadUrl: (owner: FileOwnerRef) => string;
  deleteFileUrl?: (
    owner: FileOwnerRef,
    file: AttachedFile
  ) => string;
  buildUploadPayload?: (
    context: UploadPayloadContext
  ) => Record<string, unknown>;
  resolveRemoteKey?: (remoteFile: StorageFileItemDto) => string | undefined;
  resolveRemoteId?: (remoteFile: StorageFileItemDto) => string | undefined;
  resolveRemoteName?: (remoteFile: StorageFileItemDto) => string;
}
